/*
  File name: custom.js
    Program Description:
    - This file uses the vuelidate module with added features
    - It sets all validations for each object per field, and returns error
    messages when a condition has not been met.
*/

import { validationMixin } from 'vuelidate'
import { required, minLength, email, numeric, maxLength, sameAs, minValue, requiredIf, maxValue } from 'vuelidate/lib/validators'
import { isNameLike, minDate, maxDate, fileSize, fileFormat, exactLength, rangeLength, isWhole } from './custom'
import validationErrors from './errors'

const getDate = (days, months, years) => {
  var date = new Date()
  date.setDate(date.getDate() + days)
  date.setMonth(date.getMonth() + months)
  date.setFullYear(date.getFullYear() + years)
  return date
}

const custom = {
  signup: {
    firstName: {
      required,
      maxLength: maxLength(50),
      isNameLike
    },
    lastName: {
      required,
      maxLength: maxLength(50),
      isNameLike
    },
    email: {
      email,
      required,
      maxLength: maxLength(254)
    },
    organization: {
      required
    },
    sex: {
      required
    },
    contactNumber: {
      required,
      numeric,
      rangeLength: rangeLength({ min: 10, max: 11, type: 'digits' })
    },
    selectedSector: {
      required
    }
  },
  pin: {
    numeric,
    required,
    exactLength: exactLength({ characters: 6, name: 'digits' })
  },
  backupCode: {
    numeric,
    required,
    exactLength: exactLength({ characters: 10, name: 'digits' })
  },
  users: {
    fullName: {
      required,
      maxLength: maxLength(100),
      isNameLike
    },
    firstName: {
      required,
      maxLength: maxLength(100),
      isNameLike
    },
    middleName: {
      maxLength: maxLength(30),
      isNameLike
    },
    lastName: {
      required,
      maxLength: maxLength(50),
      isNameLike
    },
    suffix: {
    },
    email: {
      email,
      required,
      maxLength: maxLength(254)
    },
    userRole: {
      required
    },
    salutation: { }, // start proponent
    agency: {
      required
    },
    organization: {
      required
    },
    designation: {
      required
    },
    position: {
      required
    },
    id: {
      front: { },
      back: { }
    },
    cellphoneNumber: {
      required,
      exactLength: exactLength({ characters: 11, name: 'digits' }),
      numeric
    },
    contactNumber: {
      required,
      numeric,
      rangeLength: rangeLength({ min: 10, max: 11, type: 'digits' })
    },
    landline: {
      rangeLength: rangeLength({ min: 8, max: 10, type: 'digits' }),
      numeric
    },
    fax: {
      rangeLength: rangeLength({ min: 8, max: 10, type: 'digits' }),
      numeric
    },
    sex: { // start libraryGuest
      required
    },
    sexOthers: { },
    birthdate: {
      required
    },
    researcherType: { },
    researcherTypeOthers: { },
    region: {
      required
    },
    province: {
      required
    },
    town: {
      required
    },
    barangay: {
      required
    },
    affiliation: { }
  },
  myProfile: {
    firstName: {
      required,
      maxLength: maxLength(100),
      isNameLike
    },
    lastName: {
      required,
      maxLength: maxLength(50),
      isNameLike
    },
    email: {
      email,
      required,
      maxLength: maxLength(254)
    },
    userRole: {
      required
    },
    division: {
      required
    },
    designation: {
      required
    },
    contactNumber: {
      numeric,
      required,
      rangeLength: rangeLength({ min: 10, max: 11, type: 'digits' })
    },
    sex: {
      required
    }
  },
  userProfile: (vm) => ({
    firstName: {
      required,
      maxLength: maxLength(100),
      isNameLike
    },
    lastName: {
      required,
      maxLength: maxLength(50),
      isNameLike
    },
    email: {
      email,
      required,
      maxLength: maxLength(254)
    },
    userRole: {
      required
    },
    division: {
      required: requiredIf(() => {
        return !vm.$s.userRole.includes('pic')
      })
    },
    designation: {
      required: requiredIf(() => {
        return !vm.$s.userRole.includes('pic')
      })
    },
    contactNumber: {
      numeric,
      required,
      rangeLength: rangeLength({ min: 10, max: 11, type: 'digits' })
    },
    sex: {
      required
    },
    reason: {
      required: requiredIf(() => {
        return vm.$s.userRole.includes('admin')
      }),
      maxLength: maxLength(300)
    },
    sector: {
      required: requiredIf(() => {
        return vm.$data.userRole.includes('pic')
      })
    },
    organization: {
      required: requiredIf(() => {
        return vm.$data.userRole.includes('pic')
      })
    }
  }),
  externalUserProfile: {
    firstName: {
      required,
      maxLength: maxLength(100),
      isNameLike
    },
    lastName: {
      required,
      maxLength: maxLength(50),
      isNameLike
    },
    email: {
      email,
      required,
      maxLength: maxLength(254)
    },
    organization: {
      required
    },
    sector: {
      required
    },
    contactNumber: {
      numeric,
      required,
      rangeLength: rangeLength({ min: 10, max: 11, type: 'digits' })
    },
    sex: {
      required
    }
  },
  expPropProfile: {
    fullName: {
      required,
      maxLength: maxLength(100),
      isNameLike
    },
    firstName: {
      required,
      maxLength: maxLength(100),
      isNameLike
    },
    middleName: {
      maxLength: maxLength(30),
      isNameLike
    },
    lastName: {
      required,
      maxLength: maxLength(50),
      isNameLike
    },
    suffix: {
    },
    email: {
      email,
      required,
      maxLength: maxLength(254)
    },
    salutation: { }, // start proponent
    agency: {
      required
    },
    organization: {
      required
    },
    sex: {
      required
    },
    designation: {
      required
    },
    position: {
      required
    },
    idPictureFront: {
      fileSize: fileSize({ max: 3 }),
      fileFormat: fileFormat({ format: ['jpg', 'jpeg', 'png'] })
    },
    idPictureBack: {
      fileSize: fileSize({ max: 3 }),
      fileFormat: fileFormat({ format: ['jpg', 'jpeg', 'png'] })
    },
    idPicture: {
      front: {
        fileSize: fileSize({ max: 3 }),
        fileFormat: fileFormat({ format: ['jpg', 'jpeg', 'png'] })
      },
      back: {
        fileSize: fileSize({ max: 3 }),
        fileFormat: fileFormat({ format: ['jpg', 'jpeg', 'png'] })
      }
    },
    cellphoneNumber: {
      numeric,
      exactLength: exactLength({ characters: 11, name: 'digits' }),
      required
    },
    contactNumber: {
      numeric,
      exactLength: exactLength({ characters: 11, name: 'digits' }),
      required
    },
    landline: {
      rangeLength: rangeLength({ min: 8, max: 10, type: 'digits' }),
      numeric
    },
    fax: {
      rangeLength: rangeLength({ min: 8, max: 10, type: 'digits' }),
      numeric
    }
  },
  reason: {
    required,
    maxLength: maxLength(300)
  },
  username: {
    minLength: minLength(5),
    required
  },
  emails: {
    $each: {
      email,
      maxLength: maxLength(254)
    }
  },
  contactNumber: {
    numeric,
    minLength: minLength(11)
  },
  assignStatus: {
    evaluator: {
      required
    },
    reviewer: {
      required
    },
    finalReviewer: {
      required
    }
  },
  deadline: {
    startMonth: {
      required
    },
    endMonth: {
      required
    }
  },
  setDeadline: {
    days: {
      required,
      minValue: minValue(1)
    }
  },
  exportModal: {
    targetDate: {
      required
    }
  },
  login: {
    password: {
      minLength: minLength(12),
      maxLength: maxLength(20),
      required
    }
  },
  currentPassword: {
    required
  },
  password: {
    required,
    valid (value) {
      const upperCase = /(?=.*?[A-Z])/.test(value)
      const lowerCase = /(?=.*?[a-z])/.test(value)
      const number = /(?=.*?[0-9])/.test(value)
      const special = /(?=.*?[!@#$%^&*.,])/.test(value)
      return (upperCase && lowerCase && number && special)
    },
    minLength: minLength(12),
    maxLength: maxLength(20)
  },
  confirmPassword: {
    required,
    sameAs: sameAs('password')
  },
  dateOfBirth: {
    required,
    minDate: minDate(getDate(0, 0, -20).toISOString().substr(0, 10)),
    maxDate: maxDate(getDate(0, 0, -7).toISOString().substr(0, 10))
  },
  notificationType: {
    value: {
      required
    }
  },
  natureOfBreach: {
    value: {
      required
    }
  },
  recordType: {
    value: {
      required
    }
  },
  dbnStatus: {
    value: {
      required
    }
  },
  sector: {
    value: {
      required
    },
    subsectors: {
      $each: {
        required
      }
    }
  },
  causeOfIncident: {
    generalCause: {
      required
    },
    specificCause: {
      $each: {
        required
      }
    }
  },
  asir: (vm) => ({
    city: {
      required
    },
    year: {
      exactLength: exactLength({ characters: 4, name: 'characters' }),
      required
    },
    mandatoryBreachNotification: {
      required,
      numeric
    },
    voluntaryBreachNotification: {
      required
    },
    summaryOthers: {
      required
    },
    theft: {
      required
    },
    identityFraud: {
      required
    },
    sabotage: {
      required

    },
    maliciousCode: {
      required

    },
    hacking: {
      required

    },
    misuseOfResources: {
      required

    },
    hardwareFailure: {
      required

    },
    softwareFailure: {
      required

    },
    communicationFailure: {
      required

    },
    naturalDisaster: {
      required

    },
    designError: {
      required

    },
    userError: {
      required

    },
    operationsError: {
      required

    },
    softwareMaintenanceError: {
      required

    },
    thirdParty: {
      required

    },
    others: {
      required
    },
    specify: {
      required: requiredIf(() => {
        return vm.formItem.others > 0
      })
    }

  }),
  pdbnf: (vm) => ({
    representative: {
      required
    },
    briefSummary: {
      required
    },
    contactInfo: {
      required,
      email
    },
    dateOfOccurrence: {
      maxDate: maxDate(vm.formItem.dateOfDiscovery, 'Date of Discovery')
    },
    dateOfDiscovery: {
      required
    },
    notificationType: {
      $each: {
        subtype: {
          required: requiredIf((obj) => {
            return obj.checkbox && obj.subTotal
          })
        },
        remarks: {
          required: requiredIf((obj) => {
            return obj.checkbox
          })
        },
        checkbox: {
          checked: _ => vm.formItem.notificationType.some(notif => notif.checkbox)
        }
      }
    },
    withRequest: {
      required
    },
    typeOfRequest: {
      required: requiredIf(() => {
        return vm.formItem.withRequest === 'Yes'
      })
    },
    requestExtension: {
      required: requiredIf(() => {
        return vm.formItem.withRequest === 'Yes' && vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes('Request for extension')
      })
    },
    justificationExemption: {
      required: requiredIf(() => {
        return vm.formItem.withRequest === 'Yes' && vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes('Request for exemption')
      })
    },
    justificationPostponement: {
      required: requiredIf(() => {
        return vm.formItem.withRequest === 'Yes' && vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes('Request for postponement')
      })
    },
    justificationAlternative: {
      required: requiredIf(() => {
        return vm.formItem.withRequest === 'Yes' && vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes('Request for alternative means of notification')
      })
    },
    justificationOthers: {
      required: requiredIf(() => {
        return vm.formItem.withRequest === 'Yes' && vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes('Others')
      })
    },
    extensionDays: {
      isWhole,
      required: requiredIf(() => {
        return vm.formItem.withRequest === 'Yes' && vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes('Request for extension')
      })
    },
    extensionStart: {
      required: requiredIf(() => {
        return vm.formItem.withRequest === 'Yes' &&
          (vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes('Request for extension')) &&
          vm.formItem.endForDocumentSubmission
      })
    },
    extensionEnd: {
      required: requiredIf(() => {
        return vm.formItem.withRequest === 'Yes' &&
          (vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes('Request for extension')) &&
          vm.formItem.endForDocumentSubmission
      })
    },
    typeOfRequestOther: {
      required: requiredIf(() => {
        return vm.formItem.withRequest === 'Yes' && vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes('Others')
      })
    },
    generalCause: {
      required
    },
    specificCause: {
      required
    },
    specificCauseOthers: {
      required: requiredIf(() => {
        return vm.formItem.specificCause === 'Others'
      })
    },
    howBreachOccurred: {
      required
    },
    chronology: {
      required
    },
    numberOfDataSubjects: {
      required
    },
    descriptionOrNature: {
      required
    },
    likelyConsequences: {
      required
    },
    dataProtectionOfficer: {
      required
    },
    sensitivePersonalInformation: {
      required
    },
    identityFraud: {
      required
    },
    measuresToAddress: {
      required
    },
    measuresToSecure: {
      required
    },
    actionsToMitigateHarm: {
      required
    },
    actionsToInformDataSubjects: {
      required
    },
    measuresToPreventRecurrence: {
      required
    },
    recordType: {
      required
    },
    dataSubjects: {
      required
    },
    specifyDataSubjectsOthers: {
      required: requiredIf(() => {
        return vm.formItem.dataSubjects && vm.formItem.dataSubjects.includes('Others')
      })
    },
    specifyDataSubjectsVulnerable: {
      required: requiredIf(() => {
        return vm.formItem.dataSubjects && vm.formItem.dataSubjects.includes('Personal data of vulnerable groups')
      })
    },
    numberOfDataSubjectsAffected: {
      required,
      minValue: minValue(0)
    }
  }),
  pdbnfEvaluate: (vm) => ({
    justified: {
      required
    },
    withRequest: {
      required
    },
    possibleViolation: {
      required
    },
    howBreachOccurredProvided: {
      required
    },
    chronologyProvided: {
      required
    },
    numberOfDataSubjectsProvided: {
      required
    },
    descriptionOrNatureProvided: {
      required
    },
    likelyConsequencesProvided: {
      required
    },
    dataProtectionOfficerProvided: {
      required
    },
    sensitivePersonalInformationProvided: {
      required
    },
    identityFraudProvided: {
      required
    },
    measuresToAddressProvided: {
      required
    },
    measuresToSecureProvided: {
      required
    },
    actionsToMitigateHarmProvided: {
      required
    },
    actionsToInformDataSubjectsProvided: {
      required
    },
    measuresToPreventRecurrenceProvided: {
      required
    },
    implementation: {
      required: requiredIf(() => {
        const vmf = vm.formItem
        return !vmf.fullReportCheckbox &&
          !vmf.privacyImpactAssessmentCheckbox &&
          !vmf.securityIncidentManagementPolicyCheckbox &&
          !vmf.operationsSecurityCheckbox &&
          !vmf.systemAcquisitionCheckbox &&
          !vmf.accessControlCheckbox &&
          !vmf.communicationsSecurityCheckbox &&
          !vmf.physicalAndEnvironmentalSecurityCheckbox &&
          !vmf.cryptographyCheckbox &&
          !vmf.assetManagementCheckbox &&
          !vmf.cryptograhpyCheckbox &&
          !vmf.humanResourceSecurityCheckbox &&
          !vmf.complianceCheckbox &&
          !vmf.informationSecurityPolicyCheckbox &&
          !vmf.dataSubjectNotificationCheckbox &&
          !vmf.delayInNotificationExplanationCheckbox &&
          !vmf.othersAttachmentCheckbox
      })
    },
    startForDocumentSubmission: {},
    endForDocumentSubmission: {},
    daysForDocumentSubmission: {
      required,
      isWhole
    },
    others: {
      required: requiredIf(() => {
        return vm.formItem.othersAttachmentCheckbox
      })
    },
    specificCauseOthers: {
      required: requiredIf(() => {
        return vm.formItem.specificCause === 'Others'
      })
    },
    notificationType: {
      $each: {
        subtype: {
          required: requiredIf(obj => obj.checkbox && obj.subTotal)
        },
        remarks: {
          required: requiredIf(obj => obj.checkbox)
        },
        checkbox: {
          checked: _ => vm.formItem.notificationType.some(notif => notif.checkbox)
        }
      }
    },
    recordType: {
      required
    },
    numberOfDataSubjectsAffected: {
      required,
      minValue: minValue(0)
    },
    dataSubjects: {
      required
    },
    specifyDataSubjectsOthers: {
      required: requiredIf(() => {
        return vm.formItem.dataSubjects && vm.formItem.dataSubjects.includes('Others')
      })
    },
    specifyDataSubjectsVulnerable: {
      required: requiredIf(() => {
        return vm.formItem.dataSubjects && vm.formItem.dataSubjects.includes('Personal data of vulnerable groups')
      })
    }
  }),
  pdbnfInitialOrder: (vm) => ({
    withRequest: {
      required
    },
    typeOfRequest: {
      required: requiredIf(() => {
        return vm.canChange && vm.formItem.withRequest === 'Yes'
      })
    },
    requestExtension: {
      required: requiredIf(() => {
        return vm.canChange && vm.formItem.withRequest === 'Yes' && vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes('Request for extension')
      })
    },
    justificationExemption: {
      required: requiredIf(() => {
        return vm.canChange && vm.formItem.withRequest === 'Yes' && vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes('Request for exemption')
      })
    },
    justificationPostponement: {
      required: requiredIf(() => {
        return vm.canChange && vm.formItem.withRequest === 'Yes' && vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes('Request for postponement')
      })
    },
    justificationAlternative: {
      required: requiredIf(() => {
        return vm.canChange && vm.formItem.withRequest === 'Yes' && vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes('Request for alternative means of notification')
      })
    },
    justificationOthers: {
      required: requiredIf(() => {
        return vm.canChange && vm.formItem.withRequest === 'Yes' && vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes('Others')
      })
    },
    extensionDays: {
      isWhole,
      required: requiredIf(() => {
        return vm.canChange && vm.formItem.withRequest === 'Yes' && vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes('Request for extension')
      })
    },
    typeOfRequestOther: {
      required: requiredIf(() => {
        return vm.canChange && vm.formItem.withRequest === 'Yes' && vm.formItem.typeOfRequest && vm.formItem.typeOfRequest.includes('Others')
      })
    },
    fullReportFile: {
      required: requiredIf(() => {
        return !vm.canChange && vm.formItem.fullReportCheckbox
      }),
      fileFormat: fileFormat({ format: ['pdf'] }),
      fileSize: fileSize({ max: 50 })
    },
    privacyImpactAssessmentFile: {
      required: requiredIf(() => {
        return !vm.canChange && vm.formItem.privacyImpactAssessmentCheckbox
      }),
      fileFormat: fileFormat({ format: ['pdf'] }),
      fileSize: fileSize({ max: 50 })
    },
    securityIncidentManagementPolicyFile: {
      required: requiredIf(() => {
        return !vm.canChange && vm.formItem.securityIncidentManagementPolicyCheckbox
      }),
      fileFormat: fileFormat({ format: ['pdf'] }),
      fileSize: fileSize({ max: 50 })
    },
    operationsSecurityFile: {
      required: requiredIf(() => {
        return !vm.canChange && vm.formItem.operationsSecurityCheckbox
      }),
      fileFormat: fileFormat({ format: ['pdf'] }),
      fileSize: fileSize({ max: 50 })
    },
    systemAcquisitionFile: {
      required: requiredIf(() => {
        return !vm.canChange && vm.formItem.systemAcquisitionCheckbox
      }),
      fileFormat: fileFormat({ format: ['pdf'] }),
      fileSize: fileSize({ max: 50 })
    },
    accessControlFile: {
      required: requiredIf(() => {
        return !vm.canChange && vm.formItem.accessControlCheckbox
      }),
      fileFormat: fileFormat({ format: ['pdf'] }),
      fileSize: fileSize({ max: 50 })
    },
    communicationsSecurityFile: {
      required: requiredIf(() => {
        return !vm.canChange && vm.formItem.communicationsSecurityCheckbox
      }),
      fileFormat: fileFormat({ format: ['pdf'] }),
      fileSize: fileSize({ max: 50 })
    },
    physicalAndEnvironmentalSecurityFile: {
      required: requiredIf(() => {
        return !vm.canChange && vm.formItem.physicalAndEnvironmentalSecurityCheckbox
      }),
      fileFormat: fileFormat({ format: ['pdf'] }),
      fileSize: fileSize({ max: 50 })
    },
    assetManagementFile: {
      required: requiredIf(() => {
        return !vm.canChange && vm.formItem.assetManagementCheckbox
      }),
      fileFormat: fileFormat({ format: ['pdf'] }),
      fileSize: fileSize({ max: 50 })
    },
    cryptographyFile: {
      required: requiredIf(() => {
        return !vm.canChange && vm.formItem.cryptographyCheckbox
      }),
      fileFormat: fileFormat({ format: ['pdf'] }),
      fileSize: fileSize({ max: 50 })
    },
    humanResourceSecurityFile: {
      required: requiredIf(() => {
        return !vm.canChange && vm.formItem.humanResourceSecurityCheckbox
      }),
      fileFormat: fileFormat({ format: ['pdf'] }),
      fileSize: fileSize({ max: 50 })
    },
    complianceFile: {
      required: requiredIf(() => {
        return !vm.canChange && vm.formItem.complianceCheckbox
      }),
      fileFormat: fileFormat({ format: ['pdf'] }),
      fileSize: fileSize({ max: 50 })
    },
    informationSecurityPolicyFile: {
      required: requiredIf(() => {
        return !vm.canChange && vm.formItem.informationSecurityPolicyCheckbox
      }),
      fileFormat: fileFormat({ format: ['pdf'] }),
      fileSize: fileSize({ max: 50 })
    },
    dataSubjectNotificationFile: {
      required: requiredIf(() => {
        return !vm.canChange && vm.formItem.dataSubjectNotificationCheckbox
      }),
      fileFormat: fileFormat({ format: ['pdf'] }),
      fileSize: fileSize({ max: 50 })
    },
    delayInNotificationExplanationFile: {
      required: requiredIf(() => {
        return !vm.canChange && vm.formItem.delayInNotificationExplanationCheckbox
      }),
      fileFormat: fileFormat({ format: ['pdf'] }),
      fileSize: fileSize({ max: 50 })
    },
    others: {
      required: requiredIf(() => {
        return !vm.canChange && vm.formItem.othersAttachmentCheckbox
      })
    },
    othersAttachmentFile: {
      required: requiredIf(() => {
        return !vm.canChange && vm.formItem.othersAttachmentCheckbox
      }),
      fileFormat: fileFormat({ format: ['doc', 'docx', 'ppt', 'pptx', 'pdf', 'xls', 'xlsx', 'png', 'jpg', 'jpeg'] }),
      fileSize: fileSize({ max: 50, totalMax: true })
    }
  }),
  fbner: (vm) => ({
    exemptionToDSNotification: {
      required
    },
    justificationForExemption: {
      required: requiredIf(function () {
        return vm.formItem.exemptionToDSNotification === 'Yes'
      })
    },
    dateOfDSNotification: {
      required: requiredIf(function () {
        return vm.formItem.notifiedDataSubject === 'Yes'
      })
    },
    modeOfDSNotification: {
      required: requiredIf(function () {
        return vm.formItem.notifiedDataSubject === 'Yes'
      })
    },
    reasonNotNotifiedDataSubject: {
      required: requiredIf(function () {
        return vm.formItem.notifiedDataSubject === 'No'
      })
    },
    dateOfSubmission: {
      required
    },
    requisites: {
      required
    },
    registration: {
      required
    },
    isIncidentInvolve: {
      required
    },
    incidentInvolve: {
      required: requiredIf(function () {
        return vm.formItem.isIncidentInvolve === 'Yes'
      })
    },
    remarksConsequences: {
      required
    },
    nameOfDpo: {
      required
    },
    officeAddress: {
      required
    },
    emailAddress: {
      email,
      required,
      maxLength: maxLength(254)
    },
    mobileOrOfficeNumber: {
      numeric,
      required,
      rangeLength: rangeLength({ min: 10, max: 11, type: 'digits' })
    },
    representativeNumber: {
      numeric,
      rangeLength: rangeLength({ min: 10, max: 11, type: 'digits' })
    },
    representativeEmail: {
      email,
      maxLength: maxLength(254)
    },
    summary: {
      required
    },
    summaryOfObservationsAndFindings: {
      required
    },
    typeOfNotification: {
      required
    },
    remarksTimeliness: {
      required
    },
    remarksCause: {
      required
    },
    natureOfBreach: {
      required
    },
    numberOfAffectedDataRecords: {
      required
    },
    sensitivePersonalInfo: {
      required
    },
    remarksBreach: {
      required
    },
    otherInfo: {
      required
    },
    remarksOtherInfo: {
      required
    },
    consequences: {
      required
    },
    organizationalFindings: {
      required
    },
    organizationalReferences: {
      required
    },
    // daysForDocumentSubmission: {
    //   required
    // },
    organizationalRecommendations: {
      required
    },
    physicalFindings: {
      required
    },
    physicalReferences: {
      required
    },
    physicalRecommendations: {
      required
    },
    technicalFindings: {
      required
    },
    technicalReferences: {
      required
    },
    technicalRecommendations: {
      required
    },
    addressFindings: {
      required
    },
    addressReferences: {
      required
    },
    addressRecommendations: {
      required
    },
    secureFindings: {
      required
    },
    secureReferences: {
      required
    },
    secureRecommendations: {
      required
    },
    mitigateFindings: {
      required
    },
    mitigateReferences: {
      required
    },
    mitigateRecommendations: {
      required
    },
    informFindings: {
      required
    },
    informReferences: {
      required
    },
    informRecommendations: {
      required
    },
    preventFindings: {
      required
    },
    preventReferences: {
      required
    },
    preventRecommendations: {
      required
    },
    recommendedAction: {
      required
    },
    remarksRecommendation: {
      required
    },
    possibleDpaViolation: {
      required
    },
    remarksDpaViolation: {
      required
    },
    initialStatusOfCompliance: {
      required
    },
    initialDateOfCompliance: {
      required
    },

    fullStatusOfCompliance: {
      required
    },
    fullDateOfCompliance: {
      required
    },

    typeOfIssuance: {
      required
    },
    dateOfIssuance: {
      required
    },
    docsArray: {
      $each: {
        document: {
          required
        }
      }
    },
    overall: {
      required
    },
    overallRecommendation: {
      required
    },
    overallRemarks: {
      required
    },
    violations: {

    },
    specifyIfSec25: {
      required: requiredIf(function () {
        return vm.formItem.violations.includes('SEC. 25. Unauthorized Processing of Personal Information and Sensitive Personal Information')
      })
    },
    specifyIfSec26: {
      required: requiredIf(function () {
        return vm.formItem.violations.includes('SEC. 26. Accessing Personal Information and Sensitive Personal Information Due to Negligence')
      })
    },
    specifyIfSec27: {
      required: requiredIf(function () {
        return vm.formItem.violations.includes('SEC. 27. Improper Disposal of Personal Information and Sensitive Personal Information')
      })
    },
    specifyIfSec28: {
      required: requiredIf(function () {
        return vm.formItem.violations.includes('SEC. 28. Processing of Personal Information and Sensitive Personal Information for Unauthorized Purposes')
      })
    },
    specifyIfSec29: {
      required: requiredIf(function () {
        return vm.formItem.violations.includes('SEC. 29. Unauthorized Access or Intentional Breach')
      })
    },
    specifyIfSec30: {
      required: requiredIf(function () {
        return vm.formItem.violations.includes('SEC. 30. Concealment of Security Breaches Involving Sensitive Personal Information')
      })
    },
    specifyIfSec31: {
      required: requiredIf(function () {
        return vm.formItem.violations.includes('SEC. 31. Malicious Disclosure')
      })
    },
    specifyIfSec32: {
      required: requiredIf(function () {
        return vm.formItem.violations.includes('SEC. 32. Unauthorized Disclosure')
      })
    },
    actionTakenArray: {
      $each: {
        actionTaken: {
          required
        },
        date: {
          required
        },
        timeliness: {
          required
        }
      }
    },
    adjudicationFileUpload: {
      required: requiredIf(function () {
        return vm.currentStatus.value === 'For Adjudication'
      }),
      fileFormat: fileFormat({ format: ['pdf'] }),
      fileSize: fileSize({ max: 50 })
    },
    investigationFileUpload: {
      required: requiredIf(function () {
        return vm.currentStatus.value === 'For Investigation'
      }),
      fileFormat: fileFormat({ format: ['pdf'] }),
      fileSize: fileSize({ max: 50 })
    },
    enforcementFileUpload: {
      required: requiredIf(function () {
        return vm.currentStatus.value === 'For Enforcement'
      }),
      fileFormat: fileFormat({ format: ['pdf'] }),
      fileSize: fileSize({ max: 50 })
    }
  }),
  pdbnfSettings: {
    days: {
      required,
      numeric
    },
    address: {
      required
    }
  },
  tableData: {
    $each: {
      timeliness: {
        required
      }
    }
  },
  ratings: (vm) => {
    return {
      quality: {
        required: requiredIf(function () {
          return vm.role !== 'Assignor'
        }),
        minValue: vm.role !== 'Assignor' ? minValue(1) : {},
        maxValue: vm.role !== 'Assignor' ? maxValue(5) : {}
      },
      efficiency: {
        required,
        minValue: minValue(1),
        maxValue: maxValue(5)
      },
      timeliness: {
        required,
        minValue: minValue(1),
        maxValue: maxValue(5)
      }
    }
  }
}
const fbnerEndorse = {
  endorsedTo: {
    required
  },
  endorseToUser: {
    required
  }
}

const endorseValidation = {
  endorsedTo: {
    required
  }
}

const pbnef = (vm) => {
  return {
    requestFor: {
      required
    },
    specify: {
      required: requiredIf(function () {
        return vm.requestFor.includes('Others')
      })
    },
    dataSubjects: {
      required
    },
    specifyIfOthers: {
      required: requiredIf(function () {
        return vm.dataSubjects.includes('Others')
      })
    },
    specifyIfGroup: {
      required: requiredIf(function () {
        return vm.dataSubjects.includes('Personal data of vulnerable groups')
      })
    },
    numberOfDataSubjectsAffected: {
      required,
      minValue: minValue(0)
    },
    atLeastOneHundredIndividuals: {},
    natureOfPersonalDataInvolved: {
      remarks: {
        required: requiredIf(function () {
          return vm.natureOfPersonalDataInvolved.checklist.includes('Sensitive Personal Information Involved') ||
                  vm.natureOfPersonalDataInvolved.checklist.includes('Information that could enable identity fraud')
        })
      },
      specify: {
        required: requiredIf(function () {
          return vm.natureOfPersonalDataInvolved.checklist.includes('Others')
        })
      },
      checklist: {
        required
      },
      additionalChecklist: {
        required: requiredIf(function () {
          return vm.natureOfPersonalDataInvolved.checklist.includes('Information that could enable identity fraud')
        })
      }
    },
    measuresInPlace: {
      remarks: {},
      checklist: {}
    },
    explanation: {
      required
    },
    evaluation: {
      required
    },
    fileUpload: {
      required: requiredIf(function () {
        return ((vm.forFinalReview && !vm.withEndorsement) || (vm.forApproval && vm.withEndorsement)) && vm.notYetApproved
      }),
      fileFormat: fileFormat({ format: ['pdf'] }),
      fileSize: fileSize({ max: 50 })
    }
  }
}

export {
  validationMixin,
  custom,
  validationErrors,
  pbnef,
  endorseValidation,
  fbnerEndorse
}
